import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  Paper,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HubIcon from '@mui/icons-material/Hub';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import FonteDadosSelector, { getFonteInicial, FonteDados } from '../../components/saude/FonteDadosSelector';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, {
  getQuadrimestreInicial,
  saveQuadrimestre,
  formatQuadrimestreParam,
} from '../../components/saude/QuadrimestreSelector';

/**
 * CVAT - Componente Vinculo e Acompanhamento Territorial (NT 30/2025 + NT 6/2025).
 *
 * Duas dimensoes: Cadastro (30%, ate 3,00 pts) e Acompanhamento (70%, ate 7,00 pts).
 * O resultado do quadrimestre e a MEDIA dos valores mensais, e so entao classificado.
 */

const CORES: Record<string, string> = {
  Otimo: '#2e7d32',
  Bom: '#1565c0',
  Suficiente: '#e65100',
  Regular: '#c62828',
};

const rotulo = (c: string) => (c === 'Otimo' ? 'Ótimo' : c);

const corDe = (c: string) => CORES[c] || '#757575';

const SaudeCvat: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipioAtivo, hasPermission } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const inicial = getQuadrimestreInicial();
  const [quadrimestre, setQuadrimestre] = useState(inicial.quadrimestre);
  const [ano, setAno] = useState(inicial.ano);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [saneamento, setSaneamento] = useState<any>(null);
  const [fonte, setFonte] = useState<FonteDados>(getFonteInicial());

  const unidadeParam = unidadeSelecionada?.codunidade;
  const qParam = formatQuadrimestreParam(quadrimestre, ano);

  useEffect(() => {
    if (!municipioAtivo?.id) { setData(null); return; }
    setLoading(true);
    setError(null);
    saudeService
      .getCvat(municipioAtivo.id, { quadrimestre: qParam, unidade: unidadeParam })
      .then((res: any) => setData(res))
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar o CVAT.'))
      .finally(() => setLoading(false));
    // `fonte` entra nas dependencias: trocar o chip precisa recarregar de verdade.
  }, [municipioAtivo?.id, qParam, unidadeParam, fonte]);

  // Saneamento carrega em paralelo e nunca bloqueia o painel principal.
  useEffect(() => {
    if (!municipioAtivo?.id) { setSaneamento(null); return; }
    saudeService
      .getCvatSaneamento(municipioAtivo.id, { quadrimestre: qParam, unidade: unidadeParam })
      .then((res: any) => setSaneamento(res))
      .catch(() => setSaneamento(null));
  }, [municipioAtivo?.id, qParam, unidadeParam, fonte]);

  const equipes = useMemo(() => (data?.equipes || []) as any[], [data]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um município para acessar o CVAT.</Alert></Box>;
  }

  const header = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          <HubIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#5e35b1' }} />
          Vínculo e Acompanhamento Territorial
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Componente CVAT do cofinanciamento federal · escore de 0 a 10 por equipe (NT 30/2025)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FonteDadosSelector
          municipioId={municipioAtivo.id}
          fonte={fonte}
          onChange={setFonte}
          quadrimestre={qParam}
          painel="cvat"
        />
        <QuadrimestreSelector
          quadrimestre={quadrimestre}
          ano={ano}
          onChange={(q, a) => { setQuadrimestre(q); setAno(a); saveQuadrimestre(q, a); }}
        />
      </Box>
    </Box>
  );

  // --- Porte nao configurado: bloqueia o painel e leva o gestor ate a tela certa ---
  if (data && data.configurado === false && data.motivo === 'porte_nao_definido') {
    const s = data.sugestao;
    const podeConfigurar = hasPermission('MANAGE_MUNICIPIOS');
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {header}
        <Alert
          severity="warning"
          action={
            podeConfigurar ? (
              <Button component={RouterLink} to="/municipios" size="small" variant="contained" startIcon={<SettingsIcon />}>
                Configurar município
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>Porte populacional não definido para {data.municipio}</AlertTitle>
          O CVAT usa o porte para determinar a população-parâmetro de cada equipe, que é o
          denominador das duas dimensões (NT 30/2025, Anexo XCIX).
          {s && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
              <Typography variant="body2">
                O Ministério da Saúde informa população de <strong>{Number(s.populacao).toLocaleString('pt-BR')}</strong>
                {s.ano_referencia ? ` (IBGE ${s.ano_referencia})` : ''} para <strong>{s.municipio_ms}</strong> —
                o que corresponde ao <strong>Porte {s.porte}</strong> ({s.porte_descricao}).
              </Typography>
            </Box>
          )}
          {!podeConfigurar && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Solicite ao administrador do sistema o preenchimento em Municípios.
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  if (data && data.dwh_implementado === false) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {header}
        <Alert severity="info">
          Este município está conectado via Data Warehouse. O CVAT ainda é calculado apenas sobre o PEC.
        </Alert>
      </Box>
    );
  }

  const m = data?.municipal;

  const cardDimensao = (
    titulo: string,
    icone: React.ReactNode,
    dim: any,
    escoreMax: number,
    detalhe: React.ReactNode,
  ) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icone}
          <Typography variant="subtitle2" fontWeight={700}>{titulo}</Typography>
          <Chip
            size="small"
            label={rotulo(dim.classificacao)}
            sx={{ ml: 'auto', bgcolor: alpha(corDe(dim.classificacao), 0.12), color: corDe(dim.classificacao), fontWeight: 700 }}
          />
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color: corDe(dim.classificacao) }}>
          {dim.percentual.toFixed(1)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dim.escore.toFixed(2)} de {escoreMax.toFixed(2)} pontos
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(dim.percentual, 100)}
          sx={{
            mt: 1, height: 8, borderRadius: 4,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            '& .MuiLinearProgress-bar': { bgcolor: corDe(dim.classificacao) },
          }}
        />
        <Box sx={{ mt: 1.5 }}>{detalhe}</Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {header}

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {unidadeSelecionada ? (
          <Chip icon={<GroupsIcon sx={{ fontSize: 16 }} />} label={unidadeSelecionada.nome} variant="outlined" />
        ) : (
          <Chip label="Todas as unidades" variant="outlined" size="small" />
        )}
        {data?.porte && (
          <Chip size="small" variant="outlined" label={`Porte ${data.porte} — ${data.porte_descricao}`} />
        )}
        {data?.quadrimestre?.meses_avaliados && (
          <Tooltip title="O resultado do quadrimestre é a média dos valores mensais (NT 6/2025). Meses ainda sem dado não entram na média.">
            <Chip
              size="small"
              variant="outlined"
              icon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
              label={`${data.quadrimestre.meses_avaliados.length} de ${data.quadrimestre.meses_do_quadrimestre.length} meses avaliados`}
            />
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && m && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              {cardDimensao(
                'Dimensão Cadastro (30%)',
                <HowToRegIcon fontSize="small" sx={{ color: '#5e35b1' }} />,
                m.cadastro,
                3,
                <Typography variant="caption" color="text.secondary" component="div">
                  {m.pessoas_cadastradas.toLocaleString('pt-BR')} pessoas cadastradas ·{' '}
                  <strong>{m.sem_vinculo_domiciliar.toLocaleString('pt-BR')}</strong> sem vínculo domiciliar
                </Typography>,
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {cardDimensao(
                'Dimensão Acompanhamento (70%)',
                <VolunteerActivismIcon fontSize="small" sx={{ color: '#5e35b1' }} />,
                m.acompanhamento,
                7,
                <Typography variant="caption" color="text.secondary" component="div">
                  {m.pessoas_acompanhadas.toLocaleString('pt-BR')} pessoas acompanhadas · população-parâmetro{' '}
                  {m.populacao_parametro.toLocaleString('pt-BR')}
                </Typography>,
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha(corDe(m.classificacao), 0.06) }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Escore final do município</Typography>
                  <Typography variant="h3" fontWeight={800} sx={{ color: corDe(m.classificacao) }}>
                    {m.escore_final.toFixed(2)}
                    <Typography component="span" variant="h6" color="text.secondary"> / 10</Typography>
                  </Typography>
                  <Chip
                    label={rotulo(m.classificacao)}
                    sx={{ mt: 1, bgcolor: corDe(m.classificacao), color: '#fff', fontWeight: 700 }}
                  />
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(data.distribuicao || {}).map(([classe, qt]) => (
                      <Chip
                        key={classe}
                        size="small"
                        variant="outlined"
                        label={`${rotulo(classe)}: ${qt}`}
                        sx={{ borderColor: corDe(classe), color: corDe(classe) }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Saneamento cadastral: onde estao os pontos a recuperar, com nome e endereco */}
          {saneamento?.cards?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                Saneamento cadastral — busca ativa e ajuste de cadastro
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Clique em um card para abrir a lista nominal, filtrar por microárea e exportar
              </Typography>
              <Grid container spacing={2}>
                {saneamento.cards.map((c: any) => (
                  <Grid item xs={12} sm={6} md={3} key={c.tipo}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%', cursor: 'pointer', transition: 'all .15s',
                        '&:hover': { borderColor: theme.palette.primary.main, boxShadow: 2 },
                      }}
                      onClick={() => navigate(`/saude/cvat/lista?tipo=${c.tipo}&quadrimestre=${qParam}`)}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{c.titulo}</Typography>
                        <Typography variant="h4" fontWeight={700} color="primary">
                          {Number(c.pessoas).toLocaleString('pt-BR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">pessoas</Typography>
                        {c.ganho_ponderado > 0 && (
                          <Chip size="small" variant="outlined" color="success" sx={{ mt: 1, display: 'flex' }}
                            label={`+${Number(c.ganho_ponderado).toLocaleString('pt-BR')} no numerador`} />
                        )}
                        {c.perda_ponderada > 0 && (
                          <Chip size="small" variant="outlined" color="error" sx={{ mt: 1, display: 'flex' }}
                            label={`−${Number(c.perda_ponderada).toLocaleString('pt-BR')} se vencer`} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {c.descricao}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Desempenho por equipe ({equipes.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ordenado do pior para o melhor escore — quem precisa de atenção aparece primeiro
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Equipe</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell align="center">Modalidade</TableCell>
                    <TableCell align="right">Parâmetro</TableCell>
                    <TableCell align="right">Cadastro</TableCell>
                    <TableCell align="right">Sem domicílio</TableCell>
                    <TableCell align="right">Acompanh.</TableCell>
                    <TableCell align="right">Escore</TableCell>
                    <TableCell align="center">Classificação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipes.map((e) => (
                    <TableRow key={e.ine} hover>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={e.equipe}>{e.equipe}</Typography>
                        <Typography variant="caption" color="text.secondary">INE {e.ine}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" noWrap title={e.unidade}>{e.unidade}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" variant="outlined" label={e.modalidade.replace('_', ' ')} />
                      </TableCell>
                      <TableCell align="right">{e.parametro_populacional.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right" sx={{ color: corDe(e.cadastro.classificacao), fontWeight: 600 }}>
                        {e.cadastro.percentual.toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        {e.cadastro.so_mici > 0 ? (
                          <Tooltip title="Pessoas com cadastro individual e sem cadastro domiciliar. Vincular ao domicílio dobra o peso da pessoa (0,75 para 1,5), sem nenhum atendimento novo.">
                            <Chip
                              size="small"
                              icon={<HomeWorkIcon sx={{ fontSize: 14 }} />}
                              label={e.cadastro.so_mici.toLocaleString('pt-BR')}
                              sx={{ bgcolor: alpha('#e65100', 0.1), color: '#e65100', fontWeight: 600 }}
                            />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: corDe(e.acompanhamento.classificacao), fontWeight: 600 }}>
                        {e.acompanhamento.percentual.toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>{e.escore_final.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={rotulo(e.classificacao)}
                          sx={{ bgcolor: alpha(corDe(e.classificacao), 0.12), color: corDe(e.classificacao), fontWeight: 700 }}
                        />
                        {e.teto_acionado && (
                          <Tooltip title="Excesso de cadastro: pela NT 30/2025 a equipe fica limitada à classificação Bom.">
                            <Chip size="small" variant="outlined" color="warning" label="teto" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Limitacoes estruturais: melhor declarar do que deixar o gestor descobrir sozinho */}
          {data.limitacoes?.length > 0 && (
            <Alert severity="info" icon={<InfoOutlinedIcon />}>
              <AlertTitle>Como ler estes números</AlertTitle>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {data.limitacoes.map((l: string, i: number) => (
                  <Typography key={i} component="li" variant="body2" sx={{ mb: 0.25 }}>{l}</Typography>
                ))}
              </Box>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default SaudeCvat;
